import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

/**
 * MarkdownRenderer — Renders Markdown content with Tailwind Typography styling.
 *
 * Uses react-markdown + remark-gfm for full GFM support:
 * headings, bold, italic, links, lists, tables, code blocks, blockquotes, etc.
 *
 * Supports special callout blockquotes:
 *   > 💡 **Dica:** ...       → indigo callout
 *   > ⚠️ **Atenção:** ...    → amber callout
 *   > 📌 **Importante:** ... → rose callout
 *   > ✅ **Exemplo:** ...    → emerald callout
 *   > 🎯 **Cenário:** ...   → violet callout
 *   > 🎬 **Vídeo:** ...     → sky callout (media reference)
 *
 * @param {string} content — Markdown string to render
 * @param {string} className — Additional CSS classes
 */

const CALLOUT_PATTERNS = [
    { emoji: '💡', keyword: 'Dica', color: 'indigo', icon: '💡' },
    { emoji: '⚠️', keyword: 'Atenção', color: 'amber', icon: '⚠️' },
    { emoji: '📌', keyword: 'Importante', color: 'rose', icon: '📌' },
    { emoji: '✅', keyword: 'Exemplo', color: 'emerald', icon: '✅' },
    { emoji: '🎯', keyword: 'Cenário', color: 'violet', icon: '🎯' },
    { emoji: '🎬', keyword: 'Vídeo', color: 'sky', icon: '🎬' },
    { emoji: '📸', keyword: 'Screenshot', color: 'sky', icon: '📸' },
    { emoji: '🔧', keyword: 'Troubleshooting', color: 'orange', icon: '🔧' },
];

const CALLOUT_COLORS = {
    indigo: 'border-indigo-500/50 bg-indigo-500/5',
    amber: 'border-amber-500/50 bg-amber-500/5',
    rose: 'border-rose-500/50 bg-rose-500/5',
    emerald: 'border-emerald-500/50 bg-emerald-500/5',
    violet: 'border-violet-500/50 bg-violet-500/5',
    sky: 'border-sky-500/50 bg-sky-500/5',
    orange: 'border-orange-500/50 bg-orange-500/5',
};

function detectCallout(children) {
    if (!children || !Array.isArray(children)) return null;
    const firstChild = children[0];
    if (!firstChild || typeof firstChild !== 'object') return null;

    // Get the text content of the blockquote
    const textContent = extractText(children);
    if (!textContent) return null;

    for (const pattern of CALLOUT_PATTERNS) {
        if (textContent.startsWith(pattern.emoji) || textContent.includes(`**${pattern.keyword}:**`)) {
            return pattern;
        }
    }
    return null;
}

function extractText(nodes) {
    if (!nodes) return '';
    if (typeof nodes === 'string') return nodes;
    if (Array.isArray(nodes)) return nodes.map(extractText).join('');
    if (nodes.props?.children) return extractText(nodes.props.children);
    return '';
}

export default function MarkdownRenderer({ content, className }) {
    if (!content) return null;

    return (
        <div
            className={cn(
                'prose prose-invert prose-sm max-w-none',
                // Headings
                'prose-headings:text-foreground-strong prose-headings:font-semibold',
                'prose-h1:text-xl prose-h1:mt-6 prose-h1:mb-3',
                'prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-2',
                'prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2',
                'prose-h4:text-sm prose-h4:mt-3 prose-h4:mb-1',
                // Paragraphs & text
                'prose-p:text-foreground-secondary prose-p:leading-relaxed prose-p:mb-3',
                'prose-strong:text-foreground-strong prose-strong:font-semibold',
                'prose-em:text-foreground-secondary',
                // Links
                'prose-a:text-indigo-400 prose-a:no-underline hover:prose-a:text-indigo-300 hover:prose-a:underline',
                // Lists
                'prose-ul:text-foreground-secondary prose-ul:mb-3',
                'prose-ol:text-foreground-secondary prose-ol:mb-3',
                'prose-li:text-foreground-secondary prose-li:mb-1',
                'prose-li:marker:text-foreground-muted',
                // Code
                'prose-code:text-indigo-300 prose-code:bg-surface-elevated prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono',
                'prose-code:before:content-none prose-code:after:content-none',
                'prose-pre:bg-surface-elevated prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:p-4',
                // Blockquotes (default)
                'prose-blockquote:border-indigo-500/40 prose-blockquote:text-foreground-muted prose-blockquote:not-italic prose-blockquote:bg-surface/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg',
                // Tables
                'prose-table:border-border',
                'prose-th:text-foreground-strong prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:text-xs prose-th:uppercase prose-th:tracking-wider',
                'prose-td:border-border prose-td:px-3 prose-td:py-2 prose-td:text-sm',
                // Horizontal rules
                'prose-hr:border-border',
                // Images
                'prose-img:rounded-xl prose-img:border prose-img:border-border prose-img:shadow-lg prose-img:my-4',
                className,
            )}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    blockquote({ children }) {
                        const callout = detectCallout(children);
                        if (callout) {
                            const colorClass = CALLOUT_COLORS[callout.color] || CALLOUT_COLORS.indigo;
                            return (
                                <div className={`not-prose border-l-4 ${colorClass} rounded-r-lg px-4 py-3 my-4`}>
                                    <div className="text-sm text-foreground-secondary [&_p]:mb-1 [&_p:last-child]:mb-0 [&_strong]:text-foreground-strong [&_code]:text-indigo-300 [&_code]:bg-surface-elevated [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_a]:text-indigo-400 [&_a:hover]:text-indigo-300">
                                        {children}
                                    </div>
                                </div>
                            );
                        }
                        return <blockquote>{children}</blockquote>;
                    },
                    img({ src, alt }) {
                        // Detect video URLs by extension
                        const isVideo = /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(src);
                        if (isVideo) {
                            return (
                                <figure className="my-6">
                                    { }
                                    <video
                                        src={src}
                                        controls
                                        preload="metadata"
                                        className="rounded-xl border border-border shadow-lg w-full"
                                    >
                                        Seu navegador não suporta vídeo.
                                    </video>
                                    {alt && alt !== src?.split('/').pop() && (
                                        <figcaption className="text-center text-xs text-foreground-muted mt-2 italic">
                                            {alt}
                                        </figcaption>
                                    )}
                                </figure>
                            );
                        }
                        return (
                            <figure className="my-6">
                                <img
                                    src={src}
                                    alt={alt || ''}
                                    className="rounded-xl border border-border shadow-lg w-full"
                                    loading="lazy"
                                />
                                {alt && (
                                    <figcaption className="text-center text-xs text-foreground-muted mt-2 italic">
                                        {alt}
                                    </figcaption>
                                )}
                            </figure>
                        );
                    },
                    a({ href, children }) {
                        const isExternal = href?.startsWith('http');
                        return (
                            <a
                                href={href}
                                target={isExternal ? '_blank' : undefined}
                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                className="text-indigo-400 hover:text-indigo-300 transition-colors"
                            >
                                {children}
                            </a>
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
}
